import { jwtDecode } from 'jwt-decode';
import { TokenStorage, useSession } from '@seedkit/vue-administration-module/public';

const AUTH_REFRESH_PATH = '/api/auth/refresh';
const AUTH_LOGOUT_PATH = '/api/auth/Logout';

let apiBaseUrl = '';
let stashedRefreshToken: string | null = null;
let endingSession = false;
let refreshPromise: Promise<boolean> | null = null;

export function setSessionApiBaseUrl(baseUrl: string): void {
  apiBaseUrl = String(baseUrl || '').replace(/\/$/, '');
}

export function getSessionApiBaseUrl(): string {
  return apiBaseUrl;
}

/** Derive API origin from a full request URL (`https://host/api/...` → `https://host`). */
export function captureApiBaseUrlFromRequestUrl(url: string): void {
  if (apiBaseUrl || typeof url !== 'string') {
    return;
  }
  const idx = url.indexOf('/api/');
  if (idx > 0) {
    setSessionApiBaseUrl(url.slice(0, idx));
  }
}

export function isAccessTokenExpired(nowSec = Math.floor(Date.now() / 1000)): boolean {
  const accessToken = TokenStorage.getAccessToken();
  if (!accessToken) {
    return true;
  }
  const expiresIn = TokenStorage.getExpiresIn();
  if (expiresIn == null) {
    return false;
  }
  return expiresIn < nowSec;
}

export function stashRefreshToken(token: string | null | undefined): void {
  if (typeof token === 'string' && token.length > 0) {
    stashedRefreshToken = token;
  }
}

export function clearStashedRefreshToken(): void {
  stashedRefreshToken = null;
}

export function getStashedRefreshToken(): string | null {
  return stashedRefreshToken;
}

/** Re-apply refresh after Seedkit clears it in `loginSuccessfully`. */
export function persistStashedRefreshToken(): void {
  if (!stashedRefreshToken) {
    return;
  }
  TokenStorage.setRefreshToken(stashedRefreshToken);
  try {
    const session = useSession();
    if (session.tokens) {
      session.tokens.refreshToken = stashedRefreshToken;
    }
  } catch {
    // Pinia may be unavailable outside app context
  }
}

export function applyAuthTokens(
  accessToken: string,
  refreshToken?: string | null,
): void {
  const expiresIn = jwtDecode<{ exp: number }>(accessToken).exp;
  TokenStorage.setAccessToken(accessToken);
  TokenStorage.setExpiresIn(expiresIn);

  if (typeof refreshToken === 'string' && refreshToken.length > 0) {
    TokenStorage.setRefreshToken(refreshToken);
    stashedRefreshToken = refreshToken;
  }

  try {
    const session = useSession();
    session.tokens.accessToken = accessToken;
    session.tokens.expiresIn = expiresIn;
    if (typeof refreshToken === 'string' && refreshToken.length > 0) {
      session.tokens.refreshToken = refreshToken;
    }
  } catch {
    // Pinia may be unavailable outside app context
  }
}

export function forceEndSession(): void {
  if (endingSession) {
    return;
  }
  endingSession = true;
  try {
    clearStashedRefreshToken();
    const session = useSession();
    session.logoutSuccessfully();
  } catch {
    TokenStorage.clearAll();
  } finally {
    queueMicrotask(() => {
      endingSession = false;
    });
  }
}

function resolveRefreshToken(): string | null {
  const stored = TokenStorage.getRefreshToken();
  if (typeof stored === 'string' && stored.length > 0) {
    return stored;
  }
  return stashedRefreshToken;
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = resolveRefreshToken();
  if (!refreshToken || !apiBaseUrl) {
    return false;
  }

  try {
    const response = await fetch(`${ apiBaseUrl }${ AUTH_REFRESH_PATH }`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json() as {
      accessToken?: string | null;
      refreshToken?: string | null;
    };

    if (!data.accessToken) {
      return false;
    }

    applyAuthTokens(data.accessToken, data.refreshToken ?? refreshToken);
    return true;
  } catch {
    return false;
  }
}

/** Single-flight refresh. Uses native `fetch` to avoid customFetch recursion. */
export async function tryRefreshSession(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * If access is still valid — true.
 * If expired — try refresh; on failure end session and return false.
 */
export async function ensureValidSession(): Promise<boolean> {
  if (!TokenStorage.getAccessToken()) {
    return false;
  }
  if (!isAccessTokenExpired()) {
    return true;
  }
  const refreshed = await tryRefreshSession();
  if (refreshed) {
    return true;
  }
  forceEndSession();
  return false;
}

/** Always clears local auth, even when Logout API fails (expired token). */
export async function secureLogout(): Promise<void> {
  try {
    const access = TokenStorage.getAccessToken();
    if (apiBaseUrl && access) {
      await fetch(`${ apiBaseUrl }${ AUTH_LOGOUT_PATH }`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${ access }` },
      }).catch(() => undefined);
    }
  } finally {
    forceEndSession();
  }
}

export function isAuthRefreshUrl(url: string): boolean {
  return /\/api\/auth\/refresh(?:\?|$)/i.test(url);
}

export function isAuthLogoutUrl(url: string): boolean {
  return /\/api\/auth\/Logout(?:\?|$)/i.test(url);
}

export function isAuthLoginResponseUrl(url: string): boolean {
  return (
    /\/api\/auth\/Login(?:\?|$)/i.test(url)
    || /\/api\/auth\/verify-sms-code(?:\?|$)/i.test(url)
    || /\/api\/auth\/vkid(?:\/callback)?(?:\?|$)/i.test(url)
    || /\/api\/auth\/yandex(?:\/callback)?(?:\?|$)/i.test(url)
  );
}

export function withUpdatedAuthorization(
  init: RequestInit | undefined,
  accessToken: string,
): RequestInit {
  const headers = new Headers(init?.headers || {});
  headers.set('Authorization', `Bearer ${ accessToken }`);
  return { ...init, headers };
}

/** Test-only: reset module-level state. */
export function __resetSessionLifecycleForTests(): void {
  apiBaseUrl = '';
  stashedRefreshToken = null;
  endingSession = false;
  refreshPromise = null;
}
